import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import NovaFeedSidebar from './NovaFeedSidebar';
import NovaAvatarImg from './NovaAvatarImg';
import { useApp } from '../context/AppContext';
import { useViewportConstrainedPanelHeight } from '../hooks/useViewportConstrainedPanelHeight';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  demoShellMobileMediaQuery,
  DEMO_RECS_OPEN_LS_KEY,
  DEMO_RECS_STRIP_WIDTH_PX,
} from '../designSystem';

const PANEL_WIDTH = 320;
const RAIL_Z = 35;
const RAIL_SHEET_Z = 200;

function readRecsOpenFromStorage() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(DEMO_RECS_OPEN_LS_KEY) === '1';
}

function useBodyScrollLock(locked) {
  useLayoutEffect(() => {
    if (!locked || typeof document === 'undefined') return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [locked]);
}

/**
 * Desktop: fixed recommendations rail (collapsible strip + overlay panel, default collapsed).
 * Mobile (≤900px): bottom FAB + full-height sheet. Does not participate in the grid as a third column.
 */
export default function NovaSidebarWithCollapse({ navigate, setNovaDraft, chapter, section, className, width: widthProp }) {
  const isMobileShell = useMediaQuery(demoShellMobileMediaQuery);
  const [recsOpen, setRecsOpen] = useState(readRecsOpenFromStorage);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { novaPresenceBar } = useApp();
  const presenceKey = novaPresenceBar ? 1 : 0;
  const { ref: panelRef } = useViewportConstrainedPanelHeight(presenceKey);
  const firstPaint = useRef(true);

  const width = widthProp ?? PANEL_WIDTH;
  const avatarChapter = chapter === 'ttc' || chapter === 'trying-to-conceive' ? 'ttc' : 'fertility';

  useBodyScrollLock(isMobileShell && sheetOpen);

  useEffect(() => {
    if (firstPaint.current) {
      firstPaint.current = false;
      return;
    }
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DEMO_RECS_OPEN_LS_KEY, recsOpen ? '1' : '0');
  }, [recsOpen]);

  useEffect(() => {
    if (!isMobileShell) setSheetOpen(false);
  }, [isMobileShell]);

  const onExpand = () => {
    setRecsOpen(true);
  };
  const onCollapse = () => {
    setRecsOpen(false);
  };

  const railInner = (
    <div
      style={{
        flex: '1 1 0%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <NovaFeedSidebar navigate={navigate} setNovaDraft={setNovaDraft} chapter={chapter} section={section} />
    </div>
  );

  if (isMobileShell) {
    return (
      <>
        <button
          type="button"
          className="demo-recs-fab"
          onClick={() => setSheetOpen(true)}
          aria-label="Open Nova daily recommendations"
        >
          <NovaAvatarImg size={26} chapter={avatarChapter} alt="" />
        </button>
        {sheetOpen && (
          <>
            <div
              role="presentation"
              onClick={() => setSheetOpen(false)}
              onKeyDown={(e) => e.key === 'Escape' && setSheetOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.4)',
                zIndex: RAIL_SHEET_Z,
              }}
            />
            <div
              role="dialog"
              aria-modal
              aria-label="Nova's daily recommendations"
              className={className}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                maxHeight: 'min(92dvh, 100%)',
                height: 'min(92dvh, 100%)',
                background: 'var(--white)',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
                zIndex: RAIL_SHEET_Z + 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  borderBottom: '0.5px solid rgba(212, 83, 126, 0.15)',
                  background: 'var(--white)',
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: '#993556',
                  }}
                >
                  NOVA&apos;S DAILY
                </span>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  aria-label="Close recommendations"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    border: '0.5px solid var(--border)',
                    background: 'var(--white)',
                    cursor: 'pointer',
                    fontSize: 20,
                    color: 'var(--muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>{railInner}</div>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <>
      {!recsOpen && (
        <button
          type="button"
          onClick={onExpand}
          aria-expanded={false}
          aria-label="Expand Nova's daily recommendations"
          style={{
            position: 'fixed',
            right: 0,
            top: 0,
            zIndex: RAIL_Z,
            width: DEMO_RECS_STRIP_WIDTH_PX,
            height: '100dvh',
            maxHeight: '100dvh',
            boxSizing: 'border-box',
            padding: '10px 0 14px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            border: 'none',
            borderLeft: '0.5px solid rgba(212,83,126,0.2)',
            background: 'var(--white)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: '-2px 0 12px rgba(0,0,0,0.04)',
          }}
        >
          <NovaAvatarImg size={28} chapter={avatarChapter} alt="" />
          <span
            style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#993556',
              marginTop: 4,
              marginBottom: 4,
            }}
          >
            NOVA&apos;S DAILY
          </span>
          <span
            style={{
              marginTop: 'auto',
              fontSize: 16,
              color: '#993556',
              lineHeight: 1,
            }}
            aria-hidden
          >
            ‹
          </span>
        </button>
      )}

      <aside
        ref={panelRef}
        className={className}
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          zIndex: RAIL_Z + 1,
          width,
          height: '100dvh',
          maxHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          background: 'var(--white)',
          borderLeft: '0.5px solid rgba(212,83,126,0.12)',
          boxShadow: recsOpen ? '-4px 0 24px rgba(0,0,0,0.1)' : 'none',
          overflow: 'hidden',
          transform: recsOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 250ms ease-out, box-shadow 250ms ease-out',
          pointerEvents: recsOpen ? 'auto' : 'none',
        }}
      >
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '6px 8px',
            borderBottom: '0.5px solid rgba(212, 83, 126, 0.12)',
            minHeight: 40,
            boxSizing: 'border-box',
            background: 'var(--white)',
          }}
        >
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Collapse recommendations"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: '0.5px solid rgba(212,83,126,0.25)',
              background: 'rgba(255,255,255,0.95)',
              color: '#993556',
              fontSize: 18,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'inherit',
          }}
          >
            ›
          </button>
        </div>
        {railInner}
      </aside>
    </>
  );
}

const SIDEBAR_WIDTH = 320;
const SIDEBAR_COLLAPSED = DEMO_RECS_STRIP_WIDTH_PX;
export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED };
